import {
  IS_IOS_AUDIO_ROUTED,
  SPEAKER_SELECTION_SUPPORTED,
} from '#features/media-window/audioRouting';
import { describeDevice, type DeviceLabelKind } from './deviceLabels';
import { loadDevicePreference } from './devicePreference';

/** Комната LiveKit глазами этой функции: переключить вход звука. */
type SwitchableRoom = {
  switchActiveDevice: (kind: DeviceLabelKind, deviceId: string) => Promise<unknown>;
};

const IS_ANDROID = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

type DeviceList = (
  kind: 'audioinput' | 'audiooutput'
) => Promise<Pick<MediaDeviceInfo, 'deviceId' | 'label'>[]>;

/** Выходы телефона без наушников: только громкий динамик и разговорный. */
const BUILT_IN_OUTPUT = new Set(['speaker', 'earpiece']);

/**
 * Громкая связь по умолчанию на Android (02.10.2026, решение владельца).
 *
 * <p>Android включает голос в режиме звонка, и без выбора звук идёт в разговорный динамик «у уха»:
 * человек, положивший телефон рядом с собой, не слышал команду и не понимал почему. Маршрут на
 * Android выбирается через вход звука – строки «Speakerphone», «Headset earpiece» и гарнитуры из
 * `enumerateDevices` переключают и микрофон, и выход сразу (см. `deviceLabels`).
 *
 * <p>Не вмешиваемся, если человек уже выбрал устройство сам (выбор запомнен) и если подключена
 * проводная или Bluetooth-гарнитура – звук и так уходит в неё. Bluetooth до 13.10.2026 был
 * исключением (у владельца это часы, и на них Android уводил разговор), но громкая связь поверх
 * наушников уводила звук из них на Mi 10T Pro. Решение владельца 13.10.2026: с Bluetooth тоже не
 * вмешиваемся – маршрут выбирает система, переключить можно в меню; риск с часами принят.
 *
 * <p>iPhone (тоже 02.10.2026): WebKit отдаёт выходы «Динамик» и «Приемник» и умеет `setSinkId`.
 * Если кроме них ничего нет – наушников и AirPods не подключено, – выбираем «Динамик». Наушники не
 * трогаем: туда звук и должен идти. Проверено только тестами – iPhone под рукой не было.
 *
 * @param list перечень устройств; пустые подписи (нет разрешения) – значит, выбрать не из чего
 */
export const applyPhoneSpeakerDefault = async (
  room: SwitchableRoom,
  list: DeviceList
): Promise<string | null> => {
  /*
    Не бросает никогда: зовётся посреди входа в голос и звонок, и любое исключение отсюда
    обрывало вход целиком – голос откатывался в «не в голосе» из-за удобства, без которого можно
    обойтись (так упали тесты на iPhone-ветке, 02.10.2026).
  */
  try {
    if (IS_IOS_AUDIO_ROUTED) {
      return await applyIphoneSpeaker(room, list);
    }
    return await applyAndroidSpeaker(room, list);
  } catch {
    return null;
  }
};

const applyAndroidSpeaker = async (room: SwitchableRoom, list: DeviceList) => {
  if (!IS_ANDROID || loadDevicePreference('mic')) {
    return null;
  }
  try {
    const inputs = await list('audioinput');
    const roles = inputs.map((device) => describeDevice('audioinput', device.label)?.role);
    if (roles.includes('wiredHeadset') || roles.includes('bluetoothHeadset')) {
      return null;
    }
    const speaker = inputs[roles.indexOf('speaker')];
    if (!speaker) {
      return null;
    }
    await room.switchActiveDevice('audioinput', speaker.deviceId);
    return speaker.deviceId;
  } catch {
    // Не вышло – остаётся маршрут системы; переключить можно в меню устройств.
    return null;
  }
};

const applyIphoneSpeaker = async (room: SwitchableRoom, list: DeviceList) => {
  if (!SPEAKER_SELECTION_SUPPORTED || loadDevicePreference('speaker')) {
    return null;
  }
  try {
    const outputs = (await list('audiooutput')).filter((device) => device.deviceId !== 'default');
    const roles = outputs.map((device) => describeDevice('audiooutput', device.label)?.role ?? '');
    if (roles.length === 0 || !roles.every((role) => BUILT_IN_OUTPUT.has(role))) {
      return null;
    }
    const speaker = outputs[roles.indexOf('speaker')];
    if (!speaker) {
      return null;
    }
    await room.switchActiveDevice('audiooutput', speaker.deviceId);
    return speaker.deviceId;
  } catch {
    return null;
  }
};
